import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import { ENVIRONMENT_CONSTANTS } from 'go-modules/constants/environment-constants.constant';

export enum RegionKey {
	AMERICAS = 'americas',
	EMEA = 'emea',
	APAC = 'apac'
}

export type RegionFlow = 'login' | 'signup';

export interface RegionOption {
	key: RegionKey;
	labelKey: string;
	domain: string;
	buttonClass: string;
	iconClass: string;
	hostMatchers: string[];
}

const REGION_STORAGE_KEY = 'gr-region-selection';
const REGION_QUERY_PARAM = 'selectRegion';
const REDIRECT_PREVENTION_KEY = 'gr-region-redirect-prevention';

@Injectable({ providedIn: 'root' })
export class RegionSelectorService {
	private _showRegionSelector$ = new BehaviorSubject<boolean>(false);
	private _canDismissRegionSelector$ = new BehaviorSubject<boolean>(true);
	private _isRedirecting$ = new BehaviorSubject<boolean>(false);

	private readonly baseOptions: RegionOption[] = [
		{
			key: RegionKey.AMERICAS,
			labelKey: 'region-selector_option-americas',
			domain: 'https://app.goreact.com',
			buttonClass: 'region-americas',
			iconClass: 'ficon-app-americas',
			hostMatchers: ['app.goreact.com', 'dev.goreact.com', 'local-app.goreact.com', 'localhost', '127.0.0.1']
		},
		{
			key: RegionKey.EMEA,
			labelKey: 'region-selector_option-emea',
			domain: 'https://app.goreact.eu',
			buttonClass: 'region-emea',
			iconClass: 'ficon-app-emea',
			hostMatchers: ['app.goreact.eu']
		},
		{
			key: RegionKey.APAC,
			labelKey: 'region-selector_option-apac',
			domain: 'https://app.goreact.au',
			buttonClass: 'region-apac',
			iconClass: 'ficon-app-apac',
			hostMatchers: ['app.goreact.au']
		}
	];

	public get showRegionSelector (): boolean {
		return this._showRegionSelector$.value;
	}

	public get showRegionSelector$ (): Observable<boolean> {
		return this._showRegionSelector$.asObservable();
	}

	public get canDismissRegionSelector (): boolean {
		return this._canDismissRegionSelector$.value;
	}

	public get canDismissRegionSelector$ (): Observable<boolean> {
		return this._canDismissRegionSelector$.asObservable();
	}

	public get isRedirecting (): boolean {
		return this._isRedirecting$.value;
	}

	public get isRedirecting$ (): Observable<boolean> {
		return this._isRedirecting$.asObservable();
	}

	public setShowRegionSelector (show: boolean): void {
		this._showRegionSelector$.next(show);
	}

	public setCanDismissRegionSelector (canDismiss: boolean): void {
		this._canDismissRegionSelector$.next(canDismiss);
	}

	public setIsRedirecting (isRedirecting: boolean): void {
		this._isRedirecting$.next(isRedirecting);
	}

	constructor (
		@Inject('Window') private window: Window,
		private translateService: TranslateService,
		private featureFlagService: NgxFeatureFlagService
	) {}

	/**
	 * Checks if the region selector feature is enabled
	 */
	private isRegionSelectorEnabled (): boolean {
		return this.featureFlagService.isAvailable('REGION_SELECTOR');
	}

	/**
	 * Builds a region URL with query params, preserving existing params and adding region prevention param
	 */
	private buildRegionUrlWithParams (
		region: RegionKey,
		flow: RegionFlow,
		existingParams: URLSearchParams
	): string {
		const option = this.getOption(region);

		const path = flow === 'signup' ? '/dashboard/auth/signup' : '/dashboard/auth/login';
		const url = new URL(path, option.domain);

		const params = existingParams;
		params.delete(REGION_QUERY_PARAM);
		params.set(REGION_QUERY_PARAM, 'false');

		url.search = params.toString();
		return url.toString();
	}

	/**
	 * Creates an observable for region label translation
	 */
	private getRegionLabel$ (region: RegionOption | undefined): Observable<string | null> {
		return this.translateService.get(region.labelKey).pipe(map((label) => label));
	}

	/**
	 * Maps environment region constant (AU, USA, EU) to RegionKey enum
	 */
	private mapEnvironmentRegionToRegionKey (envRegion: string): RegionKey | null {
		const normalized = envRegion.toUpperCase();
		switch (normalized) {
			case 'AU':
				return RegionKey.APAC;
			case 'USA':
				return RegionKey.AMERICAS;
			case 'EU':
				return RegionKey.EMEA;
			default:
				return null;
		}
	}

	/**
	 * Gets the options with environment-appropriate domains
	 */
	private getOptions (): RegionOption[] {
		if (this.isProduction()) {
			return this.baseOptions.map((option) => ({ ...option }));
		}

		const envDomain = ENVIRONMENT_CONSTANTS.APP_ENVIRONMENT === ENVIRONMENT_CONSTANTS.DEV ?
			'https://dev.goreact.com' :
			'https://local-app.goreact.com:8444';
		return this.baseOptions.map((option) => ({
			...option,
			domain: envDomain
		}));
	}

	public getOption (key: RegionKey): RegionOption | undefined {
		return this.getOptions().find((option) => option.key === key);
	}

	public getOptionForHost (host: string = this.window.location.host): RegionOption | undefined {
		const normalizedHost = host.toLowerCase();
		const options = this.getOptions();

		let matched = options.find((option) =>
			option.hostMatchers.some((matcher) => normalizedHost.includes(matcher))
		);

		if (!matched && !this.isProduction()) {
			matched = options.find((option) => option.key === RegionKey.AMERICAS);
		}

		return matched;
	}

	public getPreferredRegion (): RegionKey | null {
		return (this.window.localStorage.getItem(REGION_STORAGE_KEY) as RegionKey) || null;
	}

	public setPreferredRegion (region: RegionKey): void {
		this.window.localStorage.setItem(REGION_STORAGE_KEY, region);
	}

	public removeRegionQueryParam (): void {
		const url = new URL(this.window.location.href);
		url.searchParams.delete(REGION_QUERY_PARAM);
		const queryString = url.searchParams.toString();
		const newUrl = `${url.pathname}${queryString ? `?${queryString}` : ''}${url.hash}`;
		this.window.history.replaceState({}, '', newUrl);
	}

	/**
	 * Gets the actual current region based on APP_REGION constant or host (NOT from localStorage)
	 * Use this when you need to know what region the user is actually on, not what they prefer
	 */
	private getActualCurrentRegion (): RegionOption | undefined {
		if (ENVIRONMENT_CONSTANTS.APP_REGION) {
			const regionKey = this.mapEnvironmentRegionToRegionKey(ENVIRONMENT_CONSTANTS.APP_REGION);
			if (regionKey) {
				const regionOption = this.getOption(regionKey);
				if (regionOption) {
					return regionOption;
				}
			}
		}

		return this.getOptionForHost();
	}

	/**
	 * Gets the current region based on preferred region, APP_REGION constant, or host
	 */
	public getCurrentRegion (): RegionOption | undefined {
		const preferredRegion = this.getPreferredRegion();
		if (preferredRegion) {
			const preferredOption = this.getOption(preferredRegion);
			if (preferredOption) {
				return preferredOption;
			}
		}

		if (ENVIRONMENT_CONSTANTS.APP_REGION) {
			const regionKey = this.mapEnvironmentRegionToRegionKey(ENVIRONMENT_CONSTANTS.APP_REGION);
			if (regionKey) {
				const regionOption = this.getOption(regionKey);
				if (regionOption) {
					return regionOption;
				}
			}
		}

		return this.getOptionForHost();
	}

	/**
	 * Checks if there's a fallback-route in localStorage (indicating user has logged in before)
	 */
	private hasFallbackRoute (): boolean {
		for (let i = 0; i < this.window.localStorage.length; i++) {
			const key = this.window.localStorage.key(i);
			if (key && key.endsWith('-fallback-route')) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Checks if we should show the region selector
	 * Only shows on base routes (no query params other than selectRegion and GA params starting with _)
	 */
	public shouldShowRegionSelector (queryParams: Record<string, any> = {}): boolean {
		if (!this.isRegionSelectorEnabled()) {
			return false;
		}

		const queryValue = queryParams[REGION_QUERY_PARAM];
		if (queryValue === 'false' || queryValue === false) {
			return false;
		}

		// Don't show if there's a fallback-route in localStorage (user has logged in before)
		if (this.hasFallbackRoute()) {
			return false;
		}

		// dont show if there is an invite or other query params (except GA params starting with _)
		const otherParams = Object.keys(queryParams).filter(
			(key) => key !== REGION_QUERY_PARAM && !key.startsWith('_')
		);
		if (otherParams.length > 0) {
			return false;
		}

		const preferredRegion = this.getPreferredRegion();
		if (preferredRegion) {
			const actualCurrentRegion = this.getActualCurrentRegion();
			if (actualCurrentRegion.key === preferredRegion) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Handles the region selection flow
	 * Returns true if navigation occurred, false otherwise
	 */
	public handleRegionSelection (
		region: RegionKey,
		flow: RegionFlow
	): { shouldShow: boolean; navigated: boolean } {
		if (!this.isRegionSelectorEnabled()) {
			return { shouldShow: false, navigated: false };
		}

		const selectedOption = this.getOption(region);
		const actualCurrentRegion = this.getActualCurrentRegion();

		if (selectedOption && actualCurrentRegion.key === selectedOption.key) {
			this.setPreferredRegion(region);
			this.removeRegionQueryParam();
			return { shouldShow: false, navigated: false };
		}

		this.setPreferredRegion(region);

		if (!this.isProduction()) {
			this.removeRegionQueryParam();
			return { shouldShow: false, navigated: false };
		}

		this.setIsRedirecting(true);
		this.setShowRegionSelector(false);

		const currentUrl = new URL(this.window.location.href);
		const existingParams = new URLSearchParams(currentUrl.search);
		const url = this.buildRegionUrlWithParams(region, flow, existingParams);

		this.manageRedirectPreventionFlag('set');

		this.window.location.replace(url);

		return { shouldShow: false, navigated: true };
	}

	/**
	 * Checks if we should redirect to preferred region
	 * Also prevents infinite redirect loops
	 */
	public shouldRedirectToPreferredRegionWithPrevention (
		queryParams: Record<string, any> = {}
	): { shouldRedirect: boolean; redirectUrl?: string } {
		if (!this.isRegionSelectorEnabled()) {
			return { shouldRedirect: false };
		}

		if (this.manageRedirectPreventionFlag('check')) {
			this.manageRedirectPreventionFlag('clear');
			return { shouldRedirect: false };
		}

		const preferredRegion = this.getPreferredRegion();
		if (!preferredRegion) {
			return { shouldRedirect: false };
		}

		const actualCurrentRegion = this.getActualCurrentRegion();
		if (actualCurrentRegion.key === preferredRegion) {
			return { shouldRedirect: false };
		}

		if (!this.isProduction()) {
			return { shouldRedirect: false };
		}

		const queryValue = queryParams[REGION_QUERY_PARAM];
		if (queryValue === 'false' || queryValue === false) {
			return { shouldRedirect: false };
		}

		const currentUrl = new URL(this.window.location.href);
		const existingParams = new URLSearchParams(currentUrl.search);
		const flow = this.getFlowFromPath();
		const redirectUrl = this.buildRegionUrlWithParams(preferredRegion, flow, existingParams);

		this.manageRedirectPreventionFlag('set');

		return { shouldRedirect: true, redirectUrl };
	}

	/**
	 * Gets the flow type from the current path
	 */
	private getFlowFromPath (): RegionFlow {
		const path = this.window.location.pathname;
		return path.includes('/signup') ? 'signup' : 'login';
	}

	/**
	 * Manages redirect prevention flag to prevent infinite redirect loops
	 */
	private manageRedirectPreventionFlag (action: 'set' | 'check' | 'clear'): boolean {
		switch (action) {
			case 'set':
				this.window.sessionStorage.setItem(REDIRECT_PREVENTION_KEY, 'true');
				return true;
			case 'check':
				return this.window.sessionStorage.getItem(REDIRECT_PREVENTION_KEY) === 'true';
			case 'clear':
				this.window.sessionStorage.removeItem(REDIRECT_PREVENTION_KEY);
				return true;
		}
	}

	/**
	 * Checks if we're in a production environment
	 */
	public isProduction (): boolean {
		return ENVIRONMENT_CONSTANTS.APP_ENVIRONMENT === ENVIRONMENT_CONSTANTS.PROD;
	}

	/**
	 * Gets options for the current environment
	 * In dev/local, returns all options but they all point to the same domain.
	 * In production, returns all options with their actual region domains.
	 */
	public getOptionsForEnvironment (): RegionOption[] {
		return this.getOptions();
	}

	/**
	 * Initializes region selector state
	 * Returns the initial state and handles redirects
	 */
	public initializeRegionSelectorState (
		queryParams: Record<string, any>
	): {
		shouldRedirect: boolean;
		redirectUrl?: string;
	} {
		if (!this.isRegionSelectorEnabled()) {
			this.setShowRegionSelector(false);
			this.setCanDismissRegionSelector(true);
			return {
				shouldRedirect: false
			};
		}

		const redirectCheck = this.shouldRedirectToPreferredRegionWithPrevention(queryParams);
		if (redirectCheck.shouldRedirect && redirectCheck.redirectUrl) {
			this.setShowRegionSelector(false);
			this.setCanDismissRegionSelector(true);
			return {
				shouldRedirect: true,
				redirectUrl: redirectCheck.redirectUrl
			};
		}

		const shouldShow = this.shouldShowRegionSelector(queryParams);
		this.setShowRegionSelector(shouldShow);
		this.setCanDismissRegionSelector(shouldShow ? !this.getPreferredRegion() : true);

		if (queryParams[REGION_QUERY_PARAM]) {
			this.removeRegionQueryParam();
		}

		return {
			shouldRedirect: false
		};
	}

	/**
	 * Gets the current region info for display
	 */
	public getCurrentRegionInfo (): {
		selectedRegion: RegionOption | undefined;
		currentRegionLabel$: Observable<string | null>;
	} {
		if (!this.isRegionSelectorEnabled()) {
			return {
				selectedRegion: undefined,
				currentRegionLabel$: of(null)
			};
		}

		const selectedRegion = this.getCurrentRegion();
		return {
			selectedRegion,
			currentRegionLabel$: this.getRegionLabel$(selectedRegion)
		};
	}

	/**
	 * Handles opening the region selector
	 */
	public openRegionSelector (): void {
		if (!this.isRegionSelectorEnabled()) {
			return;
		}
		this.setShowRegionSelector(true);
		this.setCanDismissRegionSelector(true);
	}

	/**
	 * Handles dismissing the region selector
	 */
	public dismissRegionSelector (): void {
		if (!this.canDismissRegionSelector) {
			return;
		}
		this.setShowRegionSelector(false);
	}

	/**
	 * Handles region selection
	 * Returns updated state
	 */
	public handleRegionSelectionWithState (
		region: RegionKey,
		flow: RegionFlow
	): {
		navigated: boolean;
		selectedRegion: RegionOption | undefined;
		currentRegionLabel$: Observable<string | null>;
	} {
		const result = this.handleRegionSelection(region, flow);

		if (result.navigated) {
			this.setShowRegionSelector(false);
			this.setCanDismissRegionSelector(true);
			return {
				navigated: true,
				selectedRegion: undefined,
				currentRegionLabel$: of(null)
			};
		}

		const selectedRegion = this.getCurrentRegion();
		this.setShowRegionSelector(false);
		this.setCanDismissRegionSelector(true);

		return {
			navigated: false,
			selectedRegion,
			currentRegionLabel$: this.getRegionLabel$(selectedRegion)
		};
	}
}
